/** Canonical Forensic Pause banner; recovery status is its only pause fact. */

import { AnimatePresence, motion } from "framer-motion";
import { ShieldAlert } from "lucide-react";
import { useRef } from "react";

import { Button } from "@/components/ui/button";
import { TERMS } from "@/lib/terminology";

export const FORENSIC_PAUSE_BANNER_ID = "forensic-pause-banner";

interface ForensicPauseBannerProps {
  readonly active: boolean;
  readonly inProgress?: boolean;
  readonly onChooseRecovery?: () => void;
}

/**
 * Shows one security-critical fact and routes every action to the canonical
 * chooser.
 *
 * AC-61 — the banner is persistent while an incident is active: `inProgress`
 * and other props re-render it without the fact itself changing. Interrupting
 * a screen-reader user with `role="alert"`/assertive on every one of those
 * re-renders is the false urgency this rule exists to stop — so only the
 * FIRST render of a given incident announces assertively; every subsequent
 * render of the SAME incident carries `role="status"`/polite instead. A new
 * incident (active flips false -> true again) gets its own fresh assertive
 * announcement.
 *
 * Tracked with plain refs, mutated during render rather than in an effect,
 * so the downgrade is visible on the very next render — no timer, no extra
 * render pass for a screen reader (or a test) to miss.
 */
export function ForensicPauseBanner({ active, inProgress = false,
  onChooseRecovery }: ForensicPauseBannerProps): React.ReactElement | null {
  const wasActiveRef = useRef(false);
  const announcedThisIncidentRef = useRef(false);

  let isFirstAnnouncement = false;
  if (active) {
    if (!wasActiveRef.current) announcedThisIncidentRef.current = false;
    isFirstAnnouncement = !announcedThisIncidentRef.current;
    announcedThisIncidentRef.current = true;
  }
  wasActiveRef.current = active;

  const role = isFirstAnnouncement ? "alert" : "status";
  const ariaLive = isFirstAnnouncement ? "assertive" : "polite";

  return (
    <AnimatePresence>
      {active ? (
        <motion.div id={FORENSIC_PAUSE_BANNER_ID} initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="w-full border-b-2 border-red-500 bg-red-950 shadow-forensic-alert"
          role={role} aria-live={ariaLive}>
          <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
            <ShieldAlert className="size-5 shrink-0 text-red-400" aria-hidden="true" />
            <p className="min-w-0 flex-1 text-sm font-semibold leading-tight text-red-200">
              {`${TERMS.securityFreeze} on — changes are paused while your account is protected`}
            </p>
            {onChooseRecovery ? (
              <Button variant="critical" onClick={onChooseRecovery}
                className="shrink-0 px-3 text-xs">
                {inProgress ? "Continue recovery" : "Choose a recovery method"}
              </Button>
            ) : null}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
